import { useEffect, useRef, useState } from "react";
import {
  Download, ShieldCheck, Check, Trophy, Zap,
  Clock, Star, Lock, ArrowRight, Gift,
} from "lucide-react";
import confetti from "canvas-confetti";
import { useCountdown } from "../hooks/useCountdown";
import { trackEvent } from "../tracking";

const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxLEzAw8xszLtDUeLOvZCoMz7ZYKpsrzrZy94cgbHDjew_205NdVA3n6ofd_bgOvgqWdA/exec";
const XPAT_URL = "https://xpat.money/ram_miriyala/daaru_desi";


/* ─────────────────────────────────────────────
   Lead capture modal
───────────────────────────────────────────── */
function isValidPhone(value) {
  // Strip +1 (optional), spaces, and dashes — then require exactly 10 digits
  const digits = value.replace(/^\+?1/, "").replace(/\D/g, "");
  return digits.length === 10;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function LeadCaptureModal({ onComplete, onClose }) {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [committed, setCommitted] = useState(false);

  function handlePhoneChange(e) {
    setPhone(e.target.value);
    if (phoneError) setPhoneError("");
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    if (emailError) setEmailError("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    let valid = true;
    if (!isValidPhone(phone)) {
      setPhoneError("Enter a 10-digit US number (e.g. 919 308 2230 or +1 919 308 2230)");
      valid = false;
    }
    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");
      valid = false;
    }
    if (!valid) return;
    fetch(APPS_SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ phone: phone.trim(), email: email.trim(), timestamp: new Date().toLocaleString("en-US", { timeZone: "America/New_York" }) }),
    }).catch(() => {});
    trackEvent("LeadCaptured", { content_name: "lead_modal", phone: phone.trim() });
    onComplete();
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center px-3 md:px-0">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0d0d0d] border border-white/10 rounded-t-3xl md:rounded-3xl p-6 overflow-hidden mx-auto" style={{ paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom, 2.5rem))' }}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white/50 hover:bg-white/20 hover:text-white transition-colors"
          aria-label="Close"
        >
          ✕
        </button>

        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 mb-3">
            <Gift className="w-6 h-6 text-amber-400" />
          </div>
          <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/60 text-[11px] font-semibold tracking-wide">20 people claimed today</span>
          </div>
          <h3 className="text-white font-black text-xl leading-tight mb-2">Claim your $25 discount</h3>
          <p className="text-white/50 text-sm leading-relaxed">
            Drop your details — we'll WhatsApp you a reminder so you don't lose your spot.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={handleEmailChange}
              required
              className={`w-full bg-white/5 border rounded-2xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none transition-colors ${
                emailError ? "border-red-500/60 focus:border-red-500" : "border-white/10 focus:border-amber-500/50"
              }`}
            />
            {emailError && (
              <p className="text-red-400 text-xs mt-1.5 px-1">{emailError}</p>
            )}
          </div>
          <div>
            <input
              type="tel"
              placeholder="WhatsApp number (e.g. +1 919 308 2230)"
              value={phone}
              onChange={handlePhoneChange}
              required
              className={`w-full bg-white/5 border rounded-2xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none transition-colors ${
                phoneError ? "border-red-500/60 focus:border-red-500" : "border-white/10 focus:border-amber-500/50"
              }`}
            />
            {phoneError && (
              <p className="text-red-400 text-xs mt-1.5 px-1">{phoneError}</p>
            )}
          </div>
          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={committed}
                onChange={(e) => setCommitted(e.target.checked)}
                required
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${
                committed ? "bg-[#C9ED00] border-[#C9ED00]" : "bg-white/5 border-white/20"
              }`}>
                {committed && <Check className="w-3 h-3 text-black" />}
              </div>
            </div>
            <span className="text-white/50 text-xs leading-relaxed">
              I understand the KYC process takes <span className="text-white font-bold">~10 minutes</span> to complete and I commit to finishing it to claim my{" "}
              <span className="text-[#C9ED00] font-bold">$25 discount</span>
            </span>
          </label>
          <button
            type="submit"
            disabled={!committed}
            className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-base transition-all duration-200 mt-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-400"
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            Get My $25 Discount
            <ArrowRight className="w-4 h-4 flex-shrink-0" />
          </button>
          <p className="text-center text-white/20 text-[10px] tracking-widest uppercase">
            Used only to remind you about your $25 discount
          </p>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Countdown strip + urgency bar
───────────────────────────────────────────── */
function UrgencyStrip() {
  const { days, hours, mins, secs } = useCountdown();
  return (
    <div className="w-full bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 py-2.5 px-4 text-center sticky top-0 z-50 shadow-[0_2px_20px_rgba(245,158,11,0.4)]">
      {/* Mobile: compact single line — no wrapping */}
      <p className="md:hidden text-black text-[11px] font-black tracking-widest uppercase flex items-center justify-center gap-2 whitespace-nowrap">
        <Zap className="w-3 h-3 flex-shrink-0" />
        ENDS IN&nbsp;
        <span className="font-mono">
          {String(days).padStart(2,"0")}d {String(hours).padStart(2,"0")}h {String(mins).padStart(2,"0")}m
        </span>
        <Zap className="w-3 h-3 flex-shrink-0" />
      </p>
      {/* Desktop: full line */}
      <p className="hidden md:flex text-black text-xs font-black tracking-[0.2em] uppercase items-center justify-center gap-2">
        <Zap className="w-3.5 h-3.5 flex-shrink-0" />
        EXCLUSIVE OFFER EXPIRES IN&nbsp;
        <span className="font-mono">
          {String(days).padStart(2,"0")}d {String(hours).padStart(2,"0")}h {String(mins).padStart(2,"0")}m {String(secs).padStart(2,"0")}s
        </span>
        &nbsp;— ONLY {days * 24 + hours} HOURS LEFT
        <Zap className="w-3.5 h-3.5 flex-shrink-0" />
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Value line item
───────────────────────────────────────────── */
function ValueRow({ label, value, highlight }) {
  return (
    <div className={`flex items-center justify-between py-3 border-b border-white/5 last:border-0 ${highlight ? "opacity-100" : "opacity-80"}`}>
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${highlight ? "bg-[#C9ED00]" : "bg-amber-500/20"}`}>
          <Check className={`w-3 h-3 ${highlight ? "text-black" : "text-amber-400"}`} />
        </div>
        <span className={`text-sm font-medium ${highlight ? "text-white" : "text-white/70"}`}>{label}</span>
      </div>
      <span className={`text-sm font-black tabular-nums ${highlight ? "text-[#C9ED00]" : "text-amber-400"}`}>{value}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Step card
───────────────────────────────────────────── */
function Step({ number, icon: Icon, title, desc, accent }) {
  return (
    <div className="relative flex flex-col items-center text-center px-4">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-lg ${accent ? "bg-amber-500" : "bg-white/5 border border-white/10"}`}>
        <Icon className={`w-6 h-6 ${accent ? "text-black" : "text-amber-400"}`} />
      </div>
      <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#C9ED00] text-black text-[10px] font-black flex items-center justify-center">
        {number}
      </div>
      <h4 className="text-white font-bold text-sm mb-1">{title}</h4>
      <p className="text-white/40 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticket price card
───────────────────────────────────────────── */
function TicketCard({ name, original, discounted, features, featured }) {
  return (
    <div className={`relative rounded-3xl p-6 flex flex-col gap-4 ${
      featured
        ? "bg-amber-500 shadow-[0_20px_50px_rgba(245,158,11,0.35)]"
        : "bg-white/5 border border-white/10"
    }`}>
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-black rounded-full text-amber-400 text-[10px] font-black tracking-widest uppercase whitespace-nowrap">
          MOST POPULAR
        </div>
      )}
      <div>
        <p className={`text-[10px] font-bold tracking-[3px] uppercase mb-1 ${featured ? "text-black/50" : "text-white/40"}`}>Per Ticket</p>
        <h4 className={`text-xl font-serif font-bold ${featured ? "text-black" : "text-white"}`}>{name}</h4>
      </div>
      <div className="flex items-end gap-3">
        <span className={`text-4xl font-serif font-black ${featured ? "text-black" : "text-white"}`}>${discounted}</span>
        <span className={`text-lg line-through mb-1 ${featured ? "text-black/40" : "text-white/30"}`}>${original}</span>
        <span className={`text-xs font-bold mb-1.5 px-2 py-0.5 rounded-full ${featured ? "bg-black/10 text-black/60" : "bg-[#C9ED00]/10 text-[#C9ED00]"}`}>
          SAVE ${original - discounted}
        </span>
      </div>
      <ul className="space-y-2">
        {features.map((f) => (
          <li key={f} className={`flex items-center gap-2 text-xs font-medium ${featured ? "text-black/70" : "text-white/60"}`}>
            <Check className={`w-3.5 h-3.5 flex-shrink-0 ${featured ? "text-black" : "text-[#C9ED00]"}`} />
            {f}
          </li>
        ))}
      </ul>
      <p className={`text-[10px] text-center pt-1 ${featured ? "text-black/40" : "text-white/30"}`}>
        Ticket delivered to your email after payment
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
// Fires once when a DOM element first enters the viewport
function useSectionTracking(ref, eventName, data = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEvent(eventName, { content_category: "section_view", ...data });
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, eventName]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function RamFansDiscountLanding() {
  const { days, hours, mins, secs } = useCountdown();
  const hasTracked = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingStep = useRef("main");

  // Section refs for scroll-depth tracking
  const refHowItWorks  = useRef(null);
  const refValueStack  = useRef(null);
  const refPrizes      = useRef(null);
  const refPricing     = useRef(null);
  const refUrgency     = useRef(null);

  useSectionTracking(refHowItWorks, "SectionView_HowItWorks",  { content_name: "how_it_works" });
  useSectionTracking(refValueStack, "SectionView_ValueStack",  { content_name: "value_stack" });
  useSectionTracking(refPrizes,     "SectionView_Prizes",      { content_name: "prize_draw" });
  useSectionTracking(refPricing,    "SectionView_Pricing",     { content_name: "ticket_pricing" });
  useSectionTracking(refUrgency,    "SectionView_Urgency",     { content_name: "urgency_block" });

  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEvent("ExpatDiscountPageView", {
      content_name: "expat_discount_landing",
      content_category: "discount_offer",
      value: 25,
      currency: "USD",
    });
  }, []);

  function handleCTA(step = "main") {
    trackEvent("ExpatDiscountCTAClick", {
      content_name: "expat_discount_cta",
      step,
      value: 25,
      currency: "USD",
    });
    pendingStep.current = step;
    setModalOpen(true);
  }

  function handleModalComplete() {
    setModalOpen(false);
    confetti({
      particleCount: 120,
      spread: 65,
      origin: { y: 0.7 },
      colors: ["#f59e0b", "#C9ED00", "#ffffff"],
    });
    window.open(XPAT_URL, "_blank");
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col overflow-x-hidden">

      {modalOpen && <LeadCaptureModal onComplete={handleModalComplete} onClose={() => setModalOpen(false)} />}

      {/* ── Sticky urgency strip ── */}
      <UrgencyStrip />

      {/* ── Ambient background ── */}
      <div className="fixed inset-0 pointer-events-none select-none z-0">
        <img src="/heroimage.jpeg" alt="" className="w-full h-full object-cover object-top opacity-20 grayscale" aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/50 to-[#050505]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 20%, rgba(245,158,11,0.12) 0%, transparent 60%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 80% 80%, rgba(201,237,0,0.07) 0%, transparent 50%)" }} />
      </div>

      <main className="relative z-10 flex-1 container mx-auto px-5 md:px-6 max-w-2xl pb-40">

        {/* ── HERO ── */}
        <section className="pt-10 pb-12 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
            <Trophy className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-black tracking-[0.2em] text-amber-400 uppercase">Exclusive Partner Offer</span>
          </div>

          {/* Headline */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-full mb-3">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
            <span className="text-red-400 text-[11px] font-black tracking-widest uppercase">2 Days Left — May 9</span>
          </div>
          <h1 className="text-[13vw] md:text-[5.5rem] font-serif leading-[0.85] tracking-tight mb-4">
            <span className="text-[#C9ED00]">$25 OFF</span>
            <br />
            <span className="text-white">YOUR TICKET</span>
          </h1>
          <p className="text-white/50 text-base md:text-lg leading-relaxed mb-2">
            Download <strong className="text-white">xpat.money</strong> · 10-min KYC · $25 off applied instantly in the app
          </p>
          <p className="text-amber-400 text-sm font-bold">
            + Auto-entry to win <span className="text-[#C9ED00]">$20–$600</span> cash on stage — Friday night
          </p>

          {/* Event pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {["📅 May 9, 2026", "📍 Enigma Nightclub, Raleigh", "⏰ 9PM EST"].map((p) => (
              <span key={p} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-white/70 font-medium">
                {p}
              </span>
            ))}
          </div>

          {/* Hero CTA */}
          <button
            onClick={() => handleCTA("hero")}
            className="mt-8 w-full flex items-center justify-center gap-2 py-5 bg-amber-500 text-black rounded-full font-black text-base md:text-lg shadow-[0_0_40px_rgba(245,158,11,0.35)] hover:bg-amber-400 hover:scale-[1.02] transition-all duration-200"
            style={{ animation: "cta-pulse 2.5s infinite" }}
          >
            <Download className="w-5 h-5 flex-shrink-0" />
            Get $25 Off My Ticket
            <ArrowRight className="w-5 h-5 flex-shrink-0" />
          </button>
          <p className="text-white/25 text-[10px] tracking-widest uppercase mt-3">
            Free to download · KYC takes 5 minutes · $25 off applied automatically
          </p>
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5 text-[#C9ED00] flex-shrink-0" />
            <span className="text-white/50 text-[11px] font-medium">SSN is <span className="text-white font-bold">optional</span> — you can skip it and still get your $25 off</span>
          </div>

        </section>

        {/* ── HOW IT WORKS ── */}
        <section ref={refHowItWorks} className="mb-10">
          <p className="text-[10px] font-bold tracking-[3px] text-amber-500 uppercase text-center mb-6">3 steps — under 10 minutes</p>
          <div className="relative">
            <div className="absolute top-7 left-[calc(16.67%+7px)] right-[calc(16.67%+7px)] h-px bg-white/10 hidden md:block" />
            <div className="grid grid-cols-3 gap-4">
              <Step number="1" icon={Download} title="Download" desc="Install xpat.money — free on iOS & Android" accent />
              <Step number="2" icon={ShieldCheck} title="Verify" desc="Complete KYC — SSN is optional. Skip it and still get $25 off." />
              <Step number="3" icon={Gift} title="Buy Ticket" desc="$25 off applied automatically. Ticket arrives in your email." accent />
            </div>
          </div>
          <p className="text-center text-white/30 text-xs mt-6">
            Ticket confirmation sent to your email instantly after payment
          </p>

          {/* Mobile-only mid-page CTA — catches users who won't scroll further */}
          <button
            onClick={() => handleCTA("mid_page")}
            className="md:hidden mt-6 w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-sm shadow-[0_0_30px_rgba(245,158,11,0.35)]"
            style={{ animation: "cta-pulse 2.5s infinite" }}
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            Get $25 Off — 2 Days Left
            <ArrowRight className="w-4 h-4 flex-shrink-0" />
          </button>
        </section>

        {/* ── HORMOZI HOOK — desktop only ── */}
        <section className="hidden md:block mb-10">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 text-center">
            <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Think about this</p>
            <p className="text-white text-lg md:text-xl font-serif leading-relaxed">
              "You're already going to the show. You need a money app anyway.{" "}
              <span className="text-amber-400">Takes 5 minutes.</span>{" "}
              <span className="text-[#C9ED00]">You get $25 back guaranteed.</span>{" "}
              You might win up to $600 on stage. And your savings now earn 7.25% — not 4%."
            </p>
            <p className="text-white/30 text-xs mt-4">Worst case: $25 off + best APY in the market. Best case: $625+ in total value. The KYC is free.</p>
          </div>
        </section>


        {/* ── VALUE STACK ── */}
        <section ref={refValueStack} className="mb-10">
          <p className="text-[10px] font-bold tracking-[3px] text-amber-500 uppercase text-center mb-4">Everything you get</p>
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-[#C9ED00]/20 rounded-[2rem] blur opacity-60" />
            <div className="relative bg-[#0d0d0d] border border-white/10 rounded-[2rem] p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,237,0,0.06) 0%, transparent 70%)", filter: "blur(30px)" }} />

              <ValueRow label="$25 discount on your ticket" value="$25" highlight />
              <ValueRow label="Auto-entry to $20–$600 prize draw" value="Up to $600" highlight />
              <ValueRow label="7.25% APY on your USD balance" value="7.25% APY" highlight />
              <ValueRow label="Zero fees on USD → INR transfers" value="$0 fees" />
              <ValueRow label="Backed by Cross River Bank (FDIC)" value="🛡️ Secure" />
              <ValueRow label="RAM Miriyala live — May 9" value="Priceless" />

              <div className="mt-5 pt-5 border-t border-[#C9ED00]/20 flex items-center justify-between">
                <span className="text-white/40 text-xs uppercase tracking-widest">Total value</span>
                <div className="text-right">
                  <p className="text-white/30 text-xs line-through">$625+</p>
                  <p className="text-[#C9ED00] font-black text-xl">Yours FREE with KYC</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── APY COMPARISON — desktop only ── */}
        <section className="hidden md:block mb-10">
          <div className="bg-[#0d0d0d] border border-[#C9ED00]/20 rounded-3xl p-6 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,237,0,0.08) 0%, transparent 70%)", filter: "blur(20px)" }} />

            <p className="text-[10px] font-bold tracking-[3px] text-[#C9ED00] uppercase mb-1">The app that pays for itself</p>
            <h3 className="text-white font-black text-xl leading-tight mb-1">7.25% APY on your USD</h3>
            <p className="text-white/40 text-xs mb-5 leading-relaxed">
              Coming soon on xpat.money — the highest rate available. Your savings work harder while you're enjoying the show.
            </p>

            {/* Comparison table */}
            <div className="space-y-2 mb-5">
              {[
                { bank: "xpat.money", apy: "7.25%", highlight: true },
                { bank: "Most US banks", apy: "4.00–4.25%", highlight: false },
                { bank: "Big banks (Chase, BoA)", apy: "0.01%", highlight: false },
              ].map(({ bank, apy, highlight }) => (
                <div
                  key={bank}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl ${
                    highlight
                      ? "bg-[#C9ED00]/10 border border-[#C9ED00]/30"
                      : "bg-white/5 border border-white/5"
                  }`}
                >
                  <span className={`text-sm font-medium ${highlight ? "text-white font-bold" : "text-white/50"}`}>
                    {bank}
                    {highlight && <span className="ml-2 text-[9px] bg-[#C9ED00] text-black px-2 py-0.5 rounded-full font-black uppercase tracking-wide">Best</span>}
                  </span>
                  <span className={`text-sm font-black tabular-nums ${highlight ? "text-[#C9ED00]" : "text-white/30"}`}>{apy}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/5 rounded-2xl px-4 py-3 mb-3">
              <p className="text-white/60 text-xs leading-relaxed">
                💡 <span className="text-white font-bold">On $10,000:</span> xpat.money earns you <span className="text-[#C9ED00] font-black">$725/yr</span> vs $425 at a high-yield bank — <span className="text-amber-400 font-bold">$300 more per year, doing nothing.</span>
              </p>
            </div>

            <div className="bg-[#C9ED00]/10 border border-[#C9ED00]/30 rounded-2xl px-4 py-3">
              <p className="text-[9px] font-bold tracking-[2px] text-[#C9ED00] uppercase mb-1">Limited-Time Rate</p>
              <p className="text-white font-black text-base leading-tight">
                $1 USD = <span className="text-[#C9ED00]">101 Rupees</span>
              </p>
              <p className="text-white/40 text-[11px] mt-0.5">Sign up on xpat.money to lock in this rate — offer won't last.</p>
            </div>
          </div>
        </section>

        {/* ── PRIZE DRAW ── */}
        <section ref={refPrizes} className="mb-10">
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#C9ED00]/20 to-amber-500/20 rounded-[2rem] blur opacity-50" />
            <div className="relative bg-[#0d0d0d] border border-[#C9ED00]/20 rounded-[2rem] p-6 overflow-hidden">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[3px] text-[#C9ED00] uppercase mb-1">Powered by xpat.money</p>
                  <h3 className="text-white font-black text-xl leading-tight">Live Prize Giveaway</h3>
                  <p className="text-white/40 text-xs mt-1 leading-relaxed">Winners announced on stage. Your ticket = your entry. No extra signup.</p>
                </div>
                <div className="px-3 py-1.5 bg-[#C9ED00]/10 border border-[#C9ED00]/30 rounded-full flex-shrink-0 ml-4">
                  <p className="text-[#C9ED00] text-[10px] font-black tracking-widest uppercase">Free entry</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { emoji: "🎧", name: "AirPods Pro", value: "$200+" },
                  { emoji: "💳", name: "Gift Cards", value: "$20–$600" },
                  { emoji: "🎁", name: "Mystery Prize", value: "Up to $600" },
                ].map((p) => (
                  <div key={p.name} className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
                    <div className="text-2xl mb-1.5">{p.emoji}</div>
                    <p className="text-white text-[10px] font-bold leading-tight mb-1">{p.name}</p>
                    <p className="text-[#C9ED00] text-[11px] font-black">{p.value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#C9ED00]/5 border border-[#C9ED00]/15 rounded-2xl px-4 py-3 flex items-center gap-3">
                <Gift className="w-5 h-5 text-[#C9ED00] flex-shrink-0" />
                <p className="text-white text-sm font-bold leading-snug">
                  Show up. <span className="text-[#C9ED00]">You might walk out $600 richer.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── TICKET PRICING ── */}
        <section ref={refPricing} className="mb-10">
          <p className="text-[10px] font-bold tracking-[3px] text-amber-500 uppercase text-center mb-2">Ticket prices in xpat.money</p>
          <p className="text-white/30 text-xs text-center mb-6">$25 off applied automatically when you complete KYC</p>
          <div className="grid grid-cols-2 gap-4">
            <TicketCard
              name="General Admission"
              original={50}
              discounted={25}
              features={["Full concert access", "Standing room", "Food & drinks available"]}
            />
            <TicketCard
              name="VIP Standing"
              original={80}
              discounted={55}
              featured
              features={["Front of stage area", "Priority entry", "Everything in GA"]}
            />
          </div>
        </section>

        {/* ── URGENCY BLOCK ── */}
        <section ref={refUrgency} className="mb-10">
          <div className="bg-white/5 border border-amber-500/20 rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-4 justify-center">
              <Clock className="w-4 h-4 text-amber-400" />
              <p className="text-amber-400 text-[10px] font-black tracking-[3px] uppercase">Time is running out</p>
            </div>

            {/* Countdown */}
            <div className="flex justify-center gap-3 mb-5">
              {[
                { v: days, l: "Days" },
                { v: hours, l: "Hours" },
                { v: mins, l: "Mins" },
                { v: secs, l: "Secs" },
              ].map(({ v, l }) => (
                <div key={l} className="flex flex-col items-center">
                  <div className="text-3xl md:text-4xl font-serif font-bold bg-white/5 border border-white/10 rounded-2xl px-4 py-3 min-w-[60px] md:min-w-[72px] text-center text-amber-400">
                    {String(v).padStart(2, "0")}
                  </div>
                  <p className="text-[10px] uppercase tracking-[2px] text-white/30 mt-1.5">{l}</p>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              {[
                "🔴  This offer expires when the show starts — Friday 9PM",
                "⚠️  Seats are limited — Enigma holds 700–800 people",
                "⏱️  KYC takes 10 minutes — if you wait, you lose $25",
                "🎟️  Ticket confirmed instantly — no waiting, no pickup",
              ].map((line) => (
                <p key={line} className="text-white/50 text-xs text-center leading-relaxed">{line}</p>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleCTA("urgency")}
                className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-sm hover:bg-amber-400 transition-all duration-200"
              >
                <Download className="w-4 h-4 flex-shrink-0" />
                Claim $25 Off Before Friday
              </button>
            </div>
          </div>
        </section>

        {/* ── TRUST ── */}
        <section className="mb-10 flex flex-col items-center gap-4">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
            ))}
            <span className="text-white/50 text-xs ml-2 font-medium">300+ fans already visiting</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/30 font-medium">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" />Secure KYC</span>
            <span>·</span>
            <span>🛡️ FDIC-backed</span>
            <span>·</span>
            <span>Free to download</span>
            <span>·</span>
            <span>18+ event</span>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="mb-6 flex flex-col gap-3">
          <button
            onClick={() => handleCTA("bottom")}
            className="w-full flex items-center justify-center gap-2 py-5 bg-amber-500 text-black rounded-full font-black text-base md:text-lg shadow-[0_0_50px_rgba(245,158,11,0.4)] hover:bg-amber-400 hover:scale-[1.02] transition-all duration-200"
            style={{ animation: "cta-pulse 2.5s infinite" }}
          >
            <Download className="w-5 h-5 flex-shrink-0" />
            Claim $25 Off — 2 Days Left
          </button>
          <p className="text-center text-white/20 text-[10px] tracking-widest uppercase">
            Free · 5-min KYC · $25 off guaranteed · Prize entry included
          </p>
        </section>

      </main>

      {/* ── STICKY MOBILE CTA ── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden px-4 pb-6 pt-4"
        style={{ background: "linear-gradient(to top, #050505 65%, transparent)" }}
      >
        <button
          onClick={() => handleCTA("sticky")}
          className="w-full flex items-center justify-center gap-2 py-4 bg-amber-500 text-black rounded-full font-black text-sm shadow-[0_0_30px_rgba(245,158,11,0.4)]"
          style={{ animation: "cta-pulse 2.5s infinite" }}
        >
          <Download className="w-4 h-4 flex-shrink-0" />
          Get $25 Off — Friday May 9
        </button>
        <p className="text-center text-white/25 text-[9px] tracking-widest uppercase mt-2">
          ⏰ {String(days).padStart(2,"0")}d {String(hours).padStart(2,"0")}h {String(mins).padStart(2,"0")}m left · offer expires at showtime
        </p>
      </div>

      <style>{`
        @keyframes cta-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(245,158,11,0.45); }
          70%  { box-shadow: 0 0 0 16px rgba(245,158,11,0); }
          100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); }
        }
      `}</style>
    </div>
  );
}
